import React, { useState, useEffect, useRef, useCallback } from 'react'
import Button from '@material-ui/core/Button'
import PenIcon from '@material-ui/icons/Check'
import SignaturePad from 'signature_pad'

export interface IPhotoAnnotationEditorProps {
  imageBase64: string
  // Pre-annotation image. When present, Clear reverts to this instead of imageBase64.
  originalImageBase64?: string
  width?: number
  maxHeight?: number
  onSave: (annotatedBase64: string) => void
  onCancel: () => void
  saveLabel?: string
  cancelLabel?: string
  clearLabel?: string
}

const PhotoAnnotationEditor: React.FC<IPhotoAnnotationEditorProps> = (
  props: IPhotoAnnotationEditorProps
) => {
  const [penColour, setPenColour] = useState('black')
  const [didResetToOriginal, setDidResetToOriginal] = useState(false)

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const signaturePadRef = useRef<SignaturePad | null>(null)
  const aspectRatioRef = useRef(1)
  const imageSizeRef = useRef({ width: 0, height: 0 })
  const displaySizeRef = useRef({ width: 0, height: 0 })

  const calculateAspectRatio = useCallback((): Promise<number> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const ratio = img.height / img.width || 1
        aspectRatioRef.current = ratio
        imageSizeRef.current = {
          width: img.naturalWidth,
          height: img.naturalHeight
        }
        resolve(ratio)
      }
      img.onerror = () => resolve(1)
      img.src = props.imageBase64
    })
  }, [props.imageBase64])

  const loadBaseImage = useCallback(
    (source?: string): Promise<void> => {
      const src = source || props.imageBase64
      if (!signaturePadRef.current || !src) {
        return Promise.resolve()
      }

      const { width, height } = displaySizeRef.current
      return signaturePadRef.current.fromDataURL(src, {
        ratio: 1,
        width,
        height
      })
    },
    [props.imageBase64]
  )

  const initSignaturePad = useCallback(async () => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    await calculateAspectRatio()

    const containerWidth =
      props.width ||
      canvas.parentElement!.offsetWidth ||
      imageSizeRef.current.width
    const sourceWidth = imageSizeRef.current.width || containerWidth
    const sourceHeight =
      imageSizeRef.current.height ||
      Math.round(containerWidth * aspectRatioRef.current)
    const maxHeight = props.maxHeight || 600
    let displayWidth = Math.min(containerWidth, sourceWidth)
    let displayHeight = Math.round(displayWidth * aspectRatioRef.current)
    if (displayHeight > maxHeight) {
      displayHeight = maxHeight
      displayWidth = Math.round(displayHeight / (aspectRatioRef.current || 1))
    }

    if (!displayWidth || !displayHeight || !sourceWidth || !sourceHeight) {
      return
    }

    displaySizeRef.current = {
      width: displayWidth,
      height: displayHeight
    }

    canvas.width = sourceWidth
    canvas.height = sourceHeight
    canvas.style.width = `${displayWidth}px`
    canvas.style.height = `${displayHeight}px`

    const context = canvas.getContext('2d')
    if (!context) return

    context.setTransform(
      sourceWidth / displayWidth,
      0,
      0,
      sourceHeight / displayHeight,
      0,
      0
    )

    if (!signaturePadRef.current) {
      signaturePadRef.current = new SignaturePad(canvas)
    }

    signaturePadRef.current.penColor = penColour

    await loadBaseImage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.width, calculateAspectRatio, loadBaseImage])

  useEffect(() => {
    void initSignaturePad()
    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off()
        signaturePadRef.current = null
      }
    }
  }, [initSignaturePad])

  const handleSave = () => {
    if (!signaturePadRef.current) return
    const drawingData = signaturePadRef.current.toData()
    if (drawingData.length === 0 && !didResetToOriginal) {
      props.onCancel()
      return
    }
    const merged = signaturePadRef.current.toDataURL('image/png')
    props.onSave(merged)
  }

  const handleReset = () => {
    if (!signaturePadRef.current) return
    signaturePadRef.current.clear()
    if (props.originalImageBase64) {
      void loadBaseImage(props.originalImageBase64)
      setDidResetToOriginal(true)
    } else {
      void loadBaseImage()
    }
  }

  const handleSetPenColour = (colour: string) => {
    setPenColour(colour)
    if (signaturePadRef.current) {
      signaturePadRef.current.penColor = colour
    }
  }

  const colours = [
    { colour: 'black', iconColour: 'white' },
    { colour: 'red', iconColour: 'white' },
    { colour: 'yellow', iconColour: 'black' }
  ]

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 8 }}>
        {colours.map((c) => (
          <Button
            variant='contained'
            key={c.colour}
            onClick={() => handleSetPenColour(c.colour)}
            style={{
              width: 36,
              height: 36,
              marginLeft: 6,
              minWidth: 0,
              minHeight: 0,
              boxShadow:
                '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
              borderRadius: '50%',
              background: c.colour,
              color: c.iconColour
            }}
          >
            {penColour === c.colour && <PenIcon fontSize='small' />}
          </Button>
        ))}
      </div>
      <div style={{ textAlign: 'center' }}>
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: '100%',
            display: 'block',
            margin: '0 auto',
            touchAction: 'none'
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 8
        }}
      >
        <Button color='primary' onClick={props.onCancel}>
          {props.cancelLabel || 'Cancel'}
        </Button>
        <Button style={{ color: '#F00' }} onClick={handleReset}>
          {props.clearLabel || 'Clear'}
        </Button>
        <Button color='primary' onClick={handleSave}>
          {props.saveLabel || 'Save Annotation'}
        </Button>
      </div>
    </div>
  )
}

export default PhotoAnnotationEditor
